import { useMemo } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Reference } from 'types/reference';
import Image, { ImageProps } from 'frontastic/lib/image';
import Slider from 'components/commercetools-ui/atoms/slider';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import {
  desktop,
  largeDesktop,
  mediumDesktop,
  tablet,
  extraLargeDesktop,
  semiLargeDesktop,
} from 'helpers/utils/screensizes';
import { StaticImageData } from 'next/image';
import { NationalBook, NationalSemibold, Tiempos } from 'fonts';

export interface ImageSliderProps {
  data: any;
}

const ImageContentSlider: React.FC<ImageSliderProps> = ({ data }) => {
  const { isTouchDevice } = useTouchDevice();
  const [isHugeDesktop2Size] = useMediaQuery(extraLargeDesktop);
  const [isLargeDesktopSize] = useMediaQuery(largeDesktop);
  const [isMediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isMediumDesktop2Size] = useMediaQuery(semiLargeDesktop);
  const width = useWindowWidth();

  // Determine flex direction based on screen width
  // const flexDirection = useMemo(() => {
  //   if (width !== null && width < 1024) {
  //     return 'flex-col'; // Apply flex-col when screen width is less than 1024
  //   }
  //   return 'flex-row'; // Apply flex-row otherwise
  // }, [width]);

  const showArrows = useMemo(
    () =>
      !isTouchDevice &&
      !(
        (isLargeDesktopSize && data?.banner_slider?.length <= 4) ||
        (!isLargeDesktopSize && data?.banner_slider?.length <= 2)
      ),
    [isLargeDesktopSize, isTouchDevice, data?.banner_slider?.length],
  );

  return (
    <div className="image-content w-full px-20">
      <Slider
        containerClassName={showArrows ? 'px-48' : ''}
        slidesPerView={1}
        dots={width !== null && width < 1023}
        arrows={isDesktopSize}
        allowArrowsOnTouchDevice
        innerArrows
        allowTouchMove={true}
        prevButtonStyles={{
          top: '90%',
          left: isHugeDesktop2Size ? '59%' : isDesktopSize ? '65%' : width !== null && width == 1024 ? 650 : 500,
          padding: '20px',
        }}
        nextButtonStyles={{
          top: '90%',
          left: isHugeDesktop2Size ? '62%' : isDesktopSize ? '70%' : width !== null && width == 1024 ? 650 : 500,
          padding: '20px',
        }}
        className='bg-[#EDEDED]'
      >
        {data?.banner_slider?.map(
          (slide: {
            ctas: any;
            banner_desktop_image: { url: string | StaticImageData | undefined; title: string | undefined; description: string | undefined };
            banner_heading: string | undefined;
            banner_description: string | undefined;
            call_to_action: { title: string | undefined; href: string | Reference | undefined };
          }) => (
            <div key={slide.banner_heading} className={width !== null && width > 1023 ? 'flex' : 'flex flex-col'}>
              <div className="w-60%">
                {slide?.banner_desktop_image?.url ? (
                  <Image
                    loading='lazy'
                    src={slide?.banner_desktop_image?.url}
                    alt={slide?.banner_desktop_image?.description}
                    width={isTabletSize ? 1300 : 750}
                    className="brightness-75"
                  />
                ) : (
                  <div className="flex-1"></div>
                )}
              </div>
              <div className="">
                <div className="w-40% flex-1">
                  {isTabletSize ? (
                    <div
                      className={`pl-20 pt-48 md:px-40 lg:pb-50 lg:pl-48 ${
                        width !== null && width < 1023 ? 'pb-50' : ''
                      } xl:pb-100 2xl:pb-232`}
                    >
                      <div className="md:w-[90%] xl:w-[65%] 2xl:w-[55%] ">
                        <Typography
                          as="h1"
                          className={`text-left text-24 font-medium leading-[3.25rem] text-[#090909] xl:text-36 2xl:text-48 ${Tiempos.className}`}
                        >
                          {slide?.banner_heading}
                        </Typography>
                      </div>
                      <div className="xl:w-[78%] 2xl:w-[65%] ">
                        <Typography className={`mt-22 text-left text-16 font-normal leading-8 text-[#333] 2xl:text-20 ${NationalBook.className}`}>
                          {slide?.banner_description}
                        </Typography>
                      </div>
                      {slide?.ctas &&
                        slide?.ctas.map((cta: any) => (
                          <Link link={cta?.button?.href} key={cta?.button?.title} target={cta?.button?.href?.includes("https://")?'_blank' : ''}>
                            <Button
                              style={{
                                backgroundColor: cta?.button_background_color?.hex,
                                border: `1px solid ${cta?.button_border_color?.hex}`,
                                color: cta?.button_text_color?.hex,
                              }}
                              className={`mt-48 rounded-none  px-32 py-18 shadow-sm  ${
                                cta?.button_background_color?.hex?.toLowerCase() === '#ffffff'
                                  ? 'hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4]'
                                  : 'hover:!bg-[#394A54]'
                              }`}
                            >
                              <Typography as="span" className={`text-16 font-medium leading-6 text-white ${NationalSemibold.className}`}>
                                {cta?.button?.title}
                              </Typography>
                            </Button>
                          </Link>
                        ))}
                    </div>
                  ) : (
                    <div className="flex-1 bg-[#EDEDED] px-24 py-24 pb-74">
                      <Typography as="h1" className={`text-left text-24 font-medium leading-8 text-[#090909] ${Tiempos.className}`}>
                        {slide?.banner_heading}
                      </Typography>
                      <Typography className={`mt-16 text-left text-16 font-normal leading-6 text-[#090909] ${NationalBook.className}`}>
                        {slide?.banner_description}
                      </Typography>
                      {slide?.ctas &&
                        slide?.ctas.map((cta: any) => (
                          <Link link={cta?.button?.href} key={cta?.button?.title}>
                            <Button
                              style={{
                                backgroundColor: cta?.button_background_color?.hex,
                                border: `1px solid ${cta?.button_border_color?.hex}`,
                                color: cta?.button_text_color?.hex,
                              }}
                              className={` mt-24 w-full rounded-none py-18 shadow-sm  ${
                                cta?.button_background_color?.hex?.toLowerCase() === '#ffffff'
                                  ? 'hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4]'
                                  : 'hover:!bg-[#394A54]'
                              }`}
                            >
                              <Typography as="span" className="text-16 font-medium leading-6 text-white">
                                {cta?.button?.title}
                              </Typography>
                            </Button>
                          </Link>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ),
        )}
      </Slider>
    </div>
  );
};

export default ImageContentSlider;
